/**
 * AdminAuthContext.jsx — Admin Key State Management
 *
 * Stores the ABC admin key in sessionStorage (clears on tab close — more secure
 * than localStorage for a master key).
 *
 * Provides:
 *   adminKey       — current key string or null
 *   isAdminAuthed  — true if key is set
 *   loginAdmin()   — store key after validation
 *   logoutAdmin()  — clear key
 *
 * Idle timeout: same pattern as AuthContext — after IDLE_TIMEOUT_MS of no
 * mouse/keyboard/scroll activity, the admin session is cleared automatically,
 * with a SessionTimeoutModal warning shown WARNING_TIMEOUT_MS beforehand.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useIdleTimer } from '../hooks/useIdleTimer';
import SessionTimeoutModal from '../components/SessionTimeoutModal';

const SESSION_KEY = 'inqlect_admin_key';

const IDLE_TIMEOUT_MS    = 30 * 60 * 1000; // 30 minutes of inactivity
const WARNING_TIMEOUT_MS =  2 * 60 * 1000; // show warning 2 min before auto-logout

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [adminKey, setAdminKey] = useState(
    () => sessionStorage.getItem(SESSION_KEY) || null
  );
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(0);
  const warningTickRef = useRef(null);

  function loginAdmin(key) {
    sessionStorage.setItem(SESSION_KEY, key);
    setAdminKey(key);
  }

  function logoutAdmin() {
    sessionStorage.removeItem(SESSION_KEY);
    setAdminKey(null);
  }

  // ── Session timeout helpers ─────────────────────────────────────────────────

  const stopWarningTick = useCallback(() => {
    clearInterval(warningTickRef.current);
    warningTickRef.current = null;
  }, []);

  const handleIdleWarning = useCallback(() => {
    const totalSecs = Math.round(WARNING_TIMEOUT_MS / 1000);
    setWarningSecondsLeft(totalSecs);
    setShowTimeoutWarning(true);

    warningTickRef.current = setInterval(() => {
      setWarningSecondsLeft((prev) => {
        if (prev <= 1) { clearInterval(warningTickRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleIdle = useCallback(() => {
    stopWarningTick();
    setShowTimeoutWarning(false);
    logoutAdmin();
  }, [stopWarningTick]);

  const handleStayActive = useCallback(() => {
    stopWarningTick();
    setShowTimeoutWarning(false);
  }, [stopWarningTick]);

  useIdleTimer({
    idleTimeout:    IDLE_TIMEOUT_MS,
    warningTimeout: WARNING_TIMEOUT_MS,
    onWarning:      handleIdleWarning,
    onIdle:         handleIdle,
    onActive:       () => {
      if (showTimeoutWarning) { stopWarningTick(); setShowTimeoutWarning(false); }
    },
    enabled: !!adminKey,
  });

  return (
    <AdminAuthContext.Provider value={{ adminKey, isAdminAuthed: !!adminKey, loginAdmin, logoutAdmin }}>
      {children}
      {/* Session timeout warning modal — rendered at root so it always appears on top */}
      <SessionTimeoutModal
        visible={showTimeoutWarning}
        secondsLeft={warningSecondsLeft}
        onStay={handleStayActive}
        onLogout={logoutAdmin}
      />
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}
